
import { useCallback, useRef } from 'react';

export interface CallSession {
  startTime: Date;
  phoneNumber: string;
  callSid: string;
  logId: number | null;
  isLogged: boolean;
  transcript: string;
}

export const useCallLogSession = () => {
  const currentCallSessionRef = useRef<CallSession | null>(null);

  const initializeSession = useCallback((
    startTime: Date,
    phoneNumber: string,
    callSid: string,
    logId: number | null,
    initialTranscript: string = ''
  ): CallSession => {
    const newSession: CallSession = {
      startTime,
      phoneNumber,
      callSid,
      logId,
      isLogged: false,
      transcript: initialTranscript,
    };
    currentCallSessionRef.current = newSession;
    console.log('📞 Session initialized:', newSession);
    return newSession;
  }, []);

  const clearSession = useCallback(() => {
    console.log('🧹 Clearing call session. Current session before clear:', currentCallSessionRef.current);
    currentCallSessionRef.current = null;
  }, []);

  const setSessionAsLogged = useCallback(() => {
    if (currentCallSessionRef.current) {
      currentCallSessionRef.current.isLogged = true;
      console.log('🔔 Session marked as logged:', currentCallSessionRef.current);
    } else {
      console.warn('⚠️ Tried to mark session as logged, but no active session.');
    }
  }, []);

  const appendTranscript = useCallback((delta: string) => {
    if (currentCallSessionRef.current) {
      currentCallSessionRef.current.transcript += delta;
    } else {
      console.warn('⚠️ Tried to append transcript, but no active session.');
    }
  }, []);

  const getSession = useCallback((): CallSession | null => {
    return currentCallSessionRef.current;
  }, []);
  
  const hasActiveSession = useCallback((): boolean => {
    return !!currentCallSessionRef.current && currentCallSessionRef.current.logId !== null;
  }, []);

  const getActiveCallLogId = useCallback((): number | null => {
    return currentCallSessionRef.current?.logId || null;
  }, []);

  return {
    initializeSession,
    clearSession,
    setSessionAsLogged,
    appendTranscript,
    getSession,
    hasActiveSession,
    getActiveCallLogId,
  };
};
